import React, { useState, useEffect, useRef } from "react";
import arrowLeft from "../../assets/arrow-left.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Modal,
  Nav,
  Form,
  Button,
  Image,
  Row,
  Col,
  Pagination,
  Table,
  InputGroup,
  FormControl,
} from "react-bootstrap";
import productRoute from "../../services/productRoute";
import { useAppContext } from "../../AppContext";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import deleteIcon from "../../assets/delete.svg";
import edit from "../../assets/edit.svg";
import FilerobotImageEditor, { TABS, TOOLS } from "react-filerobot-image-editor";
import Select, { components } from "react-select";
import InstructionModal from "../../components/Modal/InstructionModal";
import { faChevronDown, faChevronUp, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { initial } from "lodash";
import InputGroupText from "react-bootstrap/esm/InputGroupText";

const ProductDetails = () => {
  const { productId } = useParams();
  const { showToast } = useAppContext();
  const navigate = useNavigate();

  const { state } = useLocation();
  const [initialProductData, setInitialProductData] = useState({
    productName: "",
    productSize: "",
    productDescription: "",
    productQuantity: "",
    seasonStatus: "",
    productPrice: "",
    productCategory: [],
    shoppingInstructions: [],
    productStockLevel: "",
    stockLevelAlert: "",
    productImage: null,
    dateAdded: "",
    quantitySold: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
  });
  const [productData, setProductData] = useState({
    productName: "",
    productSize: "",
    weightValue: "",
    productDescription: "",
    productQuantity: "",
    seasonStatus: "",
    productPrice: "",
    productCategory: [],
    shoppingInstructions: [],
    productStockLevel: "",
    stockLevelAlert: "",
    productImage: null,
    dateAdded: "",
    quantitySold: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    alternativeDescription: "",
  });
  const [genLoading, setGenLoading] = useState(false);
  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const typingDelay = useRef(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [instructions, setInstructions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isEditing, setIsEditing] = useState(state?.isEditing || false);
  const {
    getSingleProduct,
    editProduct,
    getProductCategory,
    getShoppingInstructions,
    generateProductDescription,
  } = productRoute();
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    getSingleProduct(productId, setLoading, showToast, setInitialProductData, setProductData);
  }, []);

  useEffect(() => {
    getProductCategory(setLoading, showToast, setCategories);
  }, []);

  useEffect(() => {
    getShoppingInstructions(setLoading, showToast, setInstructions);
  }, []);

  const generationInProgress = useRef(false);
  const metaTitleRef = useRef(null);
  const metaDescriptionRef = useRef(null);
  const productDescriptionRef = useRef(null);
  const metaKeywordsRef = useRef(null);

  const triggerDescriptionGeneration = async (name, categoriesSelected) => {
    const trimmedName = name.trim();
    if (!trimmedName || !categoriesSelected?.length) {
      showToast({
        toastText: "Please provide both product name and category",
        isSuccess: false,
      });
      return;
    }

    if (generationInProgress.current) {
      return;
    }

    const categoryName = categoriesSelected.join(", ");

    try {
      generationInProgress.current = true;

      const res = await generateProductDescription(
        { productName: trimmedName, categoryName },
        setGenLoading,
        showToast
      );

      if (res) {
        const newData = {
          metaTitle: res.meta_title || "",
          productDescription: res.description_html || "",
          metaDescription: res.meta_description || "",
          metaKeywords: Array.isArray(res.keywords) ? res.keywords.join(", ") : "",
          alternativeDescription: res.meta_description || "",
        };

        setProductData((prev) => {
          const updated = {
            ...prev,
            metaTitle: newData.metaTitle,
            productDescription: newData.productDescription,
            metaDescription: newData.metaDescription,
            metaKeywords: newData.metaKeywords,
            alternativeDescription: newData.alternativeDescription,
          };

          return updated;
        });
        setTimeout(() => {
          if (metaTitleRef.current) {
            metaTitleRef.current.value = newData.metaTitle;
          }
          if (metaDescriptionRef.current) {
            metaDescriptionRef.current.value = newData.metaTitle;
          }

          if (productDescriptionRef.current) {
            productDescriptionRef.current.value = newData.metaTitle;
          }
          if (metaKeywordsRef.current) {
            metaKeywordsRef.current.value = newData.metaKeywords;
          }
        }, 100);
      }
    } catch (err) {
      console.error("Generation failed:", err);
    } finally {
      generationInProgress.current = false;
    }
  };

  const handleEdit = async () => {
    if (typingDelay.current) {
      clearTimeout(typingDelay.current);
      typingDelay.current = null;
    }

    setIsEditing(true);

    if (
      !productData.metaTitle &&
      !productData.metaDescription &&
      !productData.metaKeywords &&
      productData.productName.trim() &&
      productData.productCategory?.length
    ) {
      setGenLoading(true);
      try {
        await triggerDescriptionGeneration(productData.productName, productData.productCategory);
      } catch (err) {
        console.error("Meta generation failed:", err);
      } finally {
        setGenLoading(false);
      }
    }
  };

  const handlePaste = () => {
    if (productData.alternativeDescription) {
      setProductData((prev) => ({
        ...prev,
        productDescription: prev.alternativeDescription,
      }));
    } else if (productData.metaDescription) {
      setProductData((prev) => ({
        ...prev,
        productDescription: prev.metaDescription,
      }));
    } else {
      showToast({
        toastText: "No generated description available to paste.",
        isSuccess: false,
      });
    }
  };

  const isFormValid = () => {
    return (
      productData?.productName &&
      productData?.productSize &&
      productData?.weightValue &&
      productData?.productQuantity &&
      productData?.productPrice &&
      productData?.productCategory.length > 0 &&
      productData?.productImage
    );
  };

  const handleBack = () => {
    if (state && state.previousPath === "/admin/products") {
      const previousPath = state.previousPath;
      const currentPage = state.currentPage;
      navigate("/admin/products", { state: { currentPage } });
    } else {
      navigate(-1);
    }
  };

  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote"],

      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],

      [{ header: [1, 2, 3, 4, 5, 6, false] }],

      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],

      ["clean"],
      ["link", "image", "video"],
    ],
  };

  const stripHtml = (html) => {
    const temporalDivElement = document.createElement("div");

    temporalDivElement.innerHTML = html;

    return temporalDivElement.textContent || temporalDivElement.innerText || "";
  };

  const hasChanges = () => {
    const simpleFields = [
      "productName",
      "productSize",
      "weightValue",
      "productQuantity",
      "seasonStatus",
      "productPrice",
      "stockLevelAlert",
      "metaTitle",
      "metaKeywords",
    ];

    for (let field of simpleFields) {
      if (productData[field] !== initialProductData[field]) {
        return true;
      }
    }

    if (
      stripHtml(productData.productDescription) !== stripHtml(initialProductData.productDescription)
    ) {
      return true;
    }

    if (stripHtml(productData.metaDescription) !== stripHtml(initialProductData.metaDescription)) {
      return true;
    }

    const arraysEqual = (a, b) =>
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((val, index) => val === b[index]);

    if (!arraysEqual(productData.productCategory, initialProductData.productCategory)) return true;
    if (!arraysEqual(productData.shoppingInstructions, initialProductData.shoppingInstructions))
      return true;

    if (productData.productImage !== initialProductData.productImage) return true;

    return false;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProductData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (value) => {
    setProductData((prev) => ({ ...prev, productDescription: value }));
  };

  const handleImageChange = (event) => {
    const MAX_SIZE = 3 * 1024 * 1024;
    const file = event.target.files[0];
    if (
      file &&
      file.type.startsWith("image/") &&
      file.size <= MAX_SIZE &&
      file.type !== "image/svg+xml"
    ) {
      const imageUrl = URL.createObjectURL(file);
      setProductData((prev) => ({ ...prev, productImage: file }));
    } else {
      showToast({
        toastText: "Invalid file. Please upload a non-SVG image file up to 10MB.",
        isSuccess: false,
      });
    }
  };

  const handleDeleteImage = () => {
    setProductData((prev) => ({ ...prev, productImage: null }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    setIsEditing(false);

    if (productData === initialProductData) {
      return;
    }

    const plainTextDescription = stripHtml(productData.productDescription);
    const countWords = (text) => {
      if (!text) return 0;
      return text
        .trim()
        .split(/\s+/) // split by one or more spaces
        .filter(Boolean).length;
    };

    // Word count validation
    const descriptionWordCount = countWords(plainTextDescription);
    const metaTitleWordCount = countWords(productData.metaTitle);
    const metaDescriptionWordCount = countWords(stripHtml(productData.metaDescription));

    if (
      productData?.productDescription &&
      (descriptionWordCount < 8 || descriptionWordCount > 400)
    ) {
      showToast({
        toastText: "Product description must be between 8 and 400 words.",
        isSuccess: false,
      });
      return;
    }

    if (productData?.metaTitle && (metaTitleWordCount < 8 || metaTitleWordCount > 60)) {
      showToast({
        toastText: "Meta title must be between 8 and 60 words.",
        isSuccess: false,
      });
      return;
    }

    if (
      productData?.metaDescription &&
      (metaDescriptionWordCount < 8 || metaDescriptionWordCount > 155)
    ) {
      showToast({
        toastText: "Meta description must be between 8 and 155 words.",
        isSuccess: false,
      });
      return;
    }

    if (productData?.metaTitle && (metaTitleWordCount < 8 || metaTitleWordCount > 60)) {
      showToast({
        toastText: "Meta title must be between 8 and 60 words.",
        isSuccess: false,
      });
      return;
    }

    if (
      productData?.metaDescription &&
      (metaDescriptionWordCount < 8 || metaDescriptionWordCount > 155)
    ) {
      showToast({
        toastText: "Meta description must be between 8 and 155 words.",
        isSuccess: false,
      });
      return;
    }

    setProductData((prevProductData) => ({
      ...prevProductData,
      productDescription: plainTextDescription,
    }));

    const categoryIds = categories
      .filter((cat) => productData.productCategory.includes(cat.categoryName))
      .map((cat) => cat._id);

    const shoppingInstructionsIds = instructions
      .filter((instruction) => productData.shoppingInstructions.includes(instruction.name))
      .map((instruction) => instruction.id);

    const formData = new FormData();
    if (productData.productName !== initialProductData.productName) {
      formData.append("productName", productData.productName);
    }
    if (
      productData.productSize !== initialProductData.productSize ||
      initialProductData.productSize === "" ||
      initialProductData.productSize === null
    ) {
      formData.append("size", productData.productSize);
    }
    if (
      productData.weightValue !== initialProductData.weightValue ||
      initialProductData.weightValue === "" ||
      initialProductData.weightValue === null
    ) {
      formData.append("weightValue", productData.weightValue);
    }
    if (productData.productQuantity !== initialProductData.productQuantity) {
      formData.append("quantityAvailable", productData.productQuantity);
    }
    if (productData.productQuantity !== 0) {
      formData.append("stockLevelAlert", productData.stockLevelAlert);
    } else if (productData.stockLevelAlert !== initialProductData.stockLevelAlert) {
      formData.append("stockLevelAlert", productData.stockLevelAlert);
    }
    if (productData.productPrice !== initialProductData.productPrice) {
      formData.append("price", productData.productPrice);
    }

    if (categoryIds.length) {
      categoryIds.forEach((categoryId) => {
        formData.append("categories", categoryId);
      });
    }

    if (shoppingInstructionsIds.length > 0) {
      shoppingInstructionsIds.forEach((id, index) => {
        formData.append(`shoppingInstructions[${index}]`, id);
      });
    } else {
      formData.append("shoppingInstructions[]", "");
    }

    if (productData.productImage !== initialProductData.productImage) {
      formData.append("productImage", productData.productImage);
    }
    if (productData.seasonStatus !== initialProductData.seasonStatus) {
      const inSeason = productData.seasonStatus === "In Season";
      formData.append("inSeason", inSeason);
    }
    if (productData.productDescription !== initialProductData.productDescription) {
      formData.append("description", plainTextDescription);
    }
    if (productData.metaTitle !== initialProductData.metaTitle)
      formData.append("metaTitle", productData.metaTitle);
    if (productData.metaDescription !== initialProductData.metaDescription)
      formData.append("metaDescription", stripHtml(productData.metaDescription));
    if (productData.metaKeywords !== initialProductData.metaKeywords) {
      productData.metaKeywords
        .split(",")
        .map((kw) => kw.trim())
        .filter(Boolean)
        .forEach((kw) => formData.append("metaKeywords[]", kw));
    }

    const contentType =
      productData.productImage !== initialProductData.productImage
        ? "multipart/form-data"
        : "application/x-www-form-urlencoded";
    editProduct(productId, formData, setLoading, showToast, contentType, state);
  };

  const options = instructions.map((instruction) => ({
    value: instruction?.name,
    label: instruction?.name,
  }));

  const CustomMenuList = (props) => {
    return (
      <components.MenuList {...props}>
        {props.children}
        <div
          style={{
            padding: "8px 12px",
            cursor: "pointer",
            borderTop: "1px solid #ddd",
            color: "#469B12",
          }}
          onClick={() => setShowInstructionModal(true)}
        >
          <FontAwesomeIcon icon={faPlus} /> Add Shopping Instruction
        </div>
      </components.MenuList>
    );
  };

  return (
    <div className="pe-3">
      <div className="d-flex gap-3 align-items-center header-admin  py-5 pe-5 ps-4">
        <img src={arrowLeft} onClick={handleBack} alt="arrow" />
        <h3 style={{ color: "#344054" }} className="my-3">
          {" "}
          Product Details
        </h3>
        {!isEditing && (
          <p
            style={{ cursor: "pointer", textDecoration: "underline", color: "#469B12" }}
            onClick={handleEdit}
            className="my-3"
          >
            Edit
          </p>
        )}
      </div>
      <div className=" pe-5 ps-4">
        {loading ? (
          <div
            className="justify-content-center align-items-center text-center"
            style={{ padding: "100px" }}
          >
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <Form onSubmit={handleSave}>
            <Row className="">
              <Col xs={6} className="pe-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Product Name:
                </p>
                <Form.Control
                  type="text"
                  name="productName"
                  value={productData.productName}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (typingDelay.current) {
                      clearTimeout(typingDelay.current);
                      typingDelay.current = null;
                    }

                    setProductData((prev) => {
                      const updatedData = { ...prev, productName: value };

                      if (isEditing && value.trim() && updatedData.productCategory?.length) {
                        typingDelay.current = setTimeout(() => {
                          triggerDescriptionGeneration(value, updatedData.productCategory);
                        }, 2000);
                      }

                      return updatedData;
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();

                      if (typingDelay.current) {
                        clearTimeout(typingDelay.current);
                        typingDelay.current = null;
                      }

                      if (productData.productName.trim() && productData.productCategory?.length) {
                        triggerDescriptionGeneration(
                          productData.productName,
                          productData.productCategory
                        );
                      }
                    }
                  }}
                  required
                  disabled={!isEditing}
                />
              </Col>
              <Col xs={6} className="pe-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  {" "}
                  Size{" "}
                </p>
                <Form.Control
                  type="text"
                  name="productSize"
                  value={productData.productSize}
                  onChange={handleChange}
                  required
                  disabled={!isEditing}
                />
              </Col>
              <Col xs={6} className="ps-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Weight Value:
                </p>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Enter weight"
                    value={productData?.weightValue}
                    onChange={(event) => {
                      let value = event.target.value;

                      if (/^[0-9]*\.?[0-9]*$/.test(value)) {
                        setProductData({ ...productData, weightValue: value });
                      }
                    }}
                    disabled={!isEditing}
                  />
                  <InputGroupText>KG</InputGroupText>
                </InputGroup>
              </Col>
              <Col xs={6} className="ps-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Category:
                </p>

                <Select
                  isMulti
                  value={categories
                    .filter((category) =>
                      productData.productCategory.includes(category.categoryName)
                    )
                    .map((category) => ({
                      label: category.categoryName,
                      value: category.categoryName,
                    }))}
                  onChange={(selectedOptions) => {
                    const newCategories = selectedOptions.map((option) => option.value);

                    if (typingDelay.current) {
                      clearTimeout(typingDelay.current);
                      typingDelay.current = null;
                    }

                    setProductData((prev) => {
                      const updatedData = { ...prev, productCategory: newCategories };

                      if (isEditing && updatedData.productName.trim() && newCategories.length) {
                        typingDelay.current = setTimeout(() => {
                          triggerDescriptionGeneration(updatedData.productName, newCategories);
                        }, 2000);
                      }

                      return updatedData;
                    });
                  }}
                  options={categories
                    .filter((category) => category.status === "active")
                    .map((category) => ({
                      value: category.categoryName,
                      label: category.categoryName,
                    }))}
                  isDisabled={!isEditing}
                />
              </Col>
              <Col xs={6} className="pe-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Price:
                </p>
                <Form.Control
                  type="text"
                  name="productPrice"
                  value={productData.productPrice}
                  onChange={(event) => {
                    const numericValue = event.target.value.replace(/\D/g, "");
                    setProductData({ ...productData, productPrice: numericValue });
                  }}
                  required
                  disabled={!isEditing}
                />
              </Col>
              <Col xs={6} className="ps-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Quantity:
                </p>
                <Form.Control
                  type="text"
                  name="productQuantity"
                  value={productData.productQuantity}
                  onChange={(event) => {
                    const numericValue = event.target.value.replace(/\D/g, "");
                    setProductData({ ...productData, productQuantity: numericValue });
                  }}
                  required
                  disabled={!isEditing}
                />
              </Col>
              {!isEditing && (
                <Col xs={6} className="pe-3 mb-4">
                  <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                    Stock Level:
                  </p>
                  <Form.Control
                    type="text"
                    name="productStockLevel"
                    value={productData.productStockLevel}
                    disabled
                  />
                </Col>
              )}
              <Col xs={6} className="ps-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Stock Level Alert:
                </p>
                <Form.Control
                  type="text"
                  name="stockLevelAlert"
                  value={productData.stockLevelAlert}
                  onChange={(event) => {
                    const numericValue = event.target.value.replace(/\D/g, "");
                    setProductData({ ...productData, stockLevelAlert: numericValue });
                  }}
                  disabled={!isEditing}
                />
              </Col>
              <Col xs={6} className="pe-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Season Status:
                </p>
                <Form.Select
                  type="text"
                  name="seasonStatus"
                  value={productData.seasonStatus}
                  onChange={handleChange}
                  disabled={!isEditing}
                >
                  <option value="">Select status</option>

                  <option value="In Season">In Season</option>
                  <option value="Off Season">Off Season</option>
                </Form.Select>
              </Col>

              <Col xs={6} className="ps-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Shopping Instructions:
                </p>

                <Select
                  isMulti
                  value={instructions
                    .filter((instruction) =>
                      productData.shoppingInstructions.includes(instruction.name)
                    )
                    .map((instruction) => ({ label: instruction.name, value: instruction.name }))}
                  options={options}
                  onChange={(selectedOptions) => {
                    const selectedValues = selectedOptions.map((option) => option.value);
                    setProductData((prev) => ({
                      ...prev,
                      shoppingInstructions: selectedValues,
                    }));
                  }}
                  noOptionsMessage={() => "No instructions available"}
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 2000,
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      zIndex: 2000,
                    }),
                  }}
                  components={{
                    MenuList: CustomMenuList,
                  }}
                  isDisabled={!isEditing}
                />
              </Col>
              {!isEditing && (
                <>
                  <Col xs={6} className="ps-3 mb-4">
                    <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                      Date Added
                    </p>
                    <Form.Control
                      type="text"
                      value={productData?.dateAdded?.split("T")[0]}
                      disabled
                    />
                  </Col>
                  <Col xs={6} className="pe-3 mb-4">
                    <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                      Quantity Sold:
                    </p>
                    <Form.Control type="text" value={productData?.quantitySold} disabled />
                  </Col>
                </>
              )}
              <Col xs={6} className="pe-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }}>
                  Meta Title{" "}
                  {genLoading && (
                    <span className="spinner-border spinner-border-sm text-success"></span>
                  )}
                </p>
                <Form.Control
                  ref={metaTitleRef}
                  type="text"
                  name="metaTitle"
                  value={productData.metaTitle || ""}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </Col>
              <Col xs={12} className="ps-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Product Description:{" "}
                  {isEditing && productData.metaDescription && (
                    <span
                      style={{ cursor: "pointer", textDecoration: "underline", color: "#469B12" }}
                      onClick={handlePaste}
                      className="my-3"
                    >
                      Paste generated description
                    </span>
                  )}
                </p>

                <ReactQuill
                  ref={productDescriptionRef}
                  value={productData.productDescription}
                  onChange={handleDescriptionChange}
                  modules={modules}
                  readOnly={!isEditing}
                />
              </Col>
              <Col xs={12} className="pe-3 mb-4">
                <p
                  style={{ color: "#333333", fontWeight: "500" }}
                  className="pb-0 mb-1 d-flex gap-2 align-items-center"
                >
                  Meta Keywords:
                  {genLoading && (
                    <span className="spinner-border spinner-border-sm text-success"></span>
                  )}
                </p>
                {(!productData.productName.trim() || !productData.productCategory?.length) && (
                  <p className="text-danger mt-1" style={{ fontSize: "13px" }}>
                    Add product name and category to enable meta description.
                  </p>
                )}

                <textarea
                  ref={metaKeywordsRef}
                  rows={3}
                  name="metaKeywords"
                  value={productData.metaKeywords || ""}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="form-control"
                />
              </Col>

              <Col xs={12} className="pe-3 mb-4">
                <p
                  style={{ color: "#333333", fontWeight: "500" }}
                  className="pb-0 mb-1 d-flex gap-2 align-items-center"
                >
                  Meta Description:{" "}
                  {isEditing && !genLoading && productData.metaDescription && (
                    <span
                      style={{
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: "#469B12",
                      }}
                      onClick={() =>
                        triggerDescriptionGeneration(
                          productData.productName,
                          productData.productCategory
                        )
                      }
                      className="my-3"
                    >
                      Regenerate
                    </span>
                  )}
                  {genLoading && (
                    <span className="spinner-border spinner-border-sm text-success"></span>
                  )}
                </p>
                {(!productData.productName.trim() || !productData.productCategory?.length) && (
                  <p className="text-danger mt-1" style={{ fontSize: "13px" }}>
                    Add product name and category to enable meta description.
                  </p>
                )}

                <ReactQuill
                  ref={metaDescriptionRef}
                  value={productData.metaDescription}
                  onChange={(value) =>
                    setProductData((prev) => ({ ...prev, metaDescription: value }))
                  }
                  modules={modules}
                  readOnly={!isEditing}
                />
              </Col>
            </Row>

            <div className="my-4">
              {productData.productImage ? (
                <>
                  <Button className="mb-4">Attached Image</Button>
                  <div className="d-flex gap-2">
                    <img
                      src={
                        typeof productData.productImage === "string"
                          ? productData.productImage
                          : URL.createObjectURL(productData.productImage)
                      }
                      alt="Product"
                      style={{ width: "100px", height: "100px" }}
                    />
                    {isEditing && (
                      <div className="d-flex align-items-end">
                        <img
                          src={deleteIcon}
                          onClick={handleDeleteImage}
                          style={{
                            width: "24px",
                            height: "24px",
                            cursor: "pointer",
                            marginLeft: "15px",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Form.Control
                    type="file"
                    onChange={handleImageChange}
                    accept="image/*"
                    style={{ display: "none" }}
                    id="fileInput"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => document.getElementById("fileInput").click()}
                    style={{ backgroundColor: "#CCCDCF", border: "none", borderRadius: "4px" }}
                    className="px-4"
                  >
                    Upload Image
                  </Button>
                </>
              )}
            </div>

            {isEditing && (
              <div className="pt-3 my-3 d-flex justify-content-end">
                <Button
                  className="px-5 py-2"
                  disabled={!isFormValid() || !hasChanges()}
                  style={{
                    backgroundColor:
                      isFormValid() && hasChanges() ? "#469B12" : "rgba(70, 155, 18, 0.2)",
                    color: "#F6F6F6",
                    boxShadow: "0px 4px 4px 0px #0E35BF40",
                  }}
                  type="submit"
                >
                  Save
                </Button>
              </div>
            )}
          </Form>
        )}
      </div>
      <InstructionModal
        showInstructionModal={showInstructionModal}
        setShowInstructionModal={setShowInstructionModal}
      />
    </div>
  );
};

export default ProductDetails;
